import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Briefcase, LogOut, Clock, CheckCircle, AlertCircle, Loader2,
  FileText, MessageSquare, ChevronRight, User, Phone, MapPin,
  Download, Send, ArrowLeft, Menu, X
} from 'lucide-react';

function CaseCard({ caseItem, onSelect }) {
  const statusColors = {
    intake: 'bg-yellow-500/20 text-yellow-400',
    draft: 'bg-blue-500/20 text-blue-400',
    assigned: 'bg-purple-500/20 text-purple-400',
    review: 'bg-orange-500/20 text-orange-400',
    completed: 'bg-green-500/20 text-green-400',
  };

  return (
    <div 
      onClick={() => onSelect(caseItem)}
      className="bg-[#111b21] rounded-xl border border-[#2a3942] p-4 cursor-pointer hover:border-[#00a884] transition"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-white font-mono text-sm">{caseItem.case_number || caseItem.id?.slice(0, 8)}</p>
          <p className="text-[#8696a0] text-xs mt-1">{caseItem.service_type || 'General'}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs ${statusColors[caseItem.status] || 'bg-gray-500/20 text-gray-400'}`}>
          {caseItem.status}
        </span>
      </div>
      <div className="flex items-center gap-4 text-xs text-[#8696a0]">
        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {caseItem.client_name || 'Anonymous'}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(caseItem.created_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

export default function LawyerPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assigned'); // assigned | all
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [documentHtml, setDocumentHtml] = useState(null);
  const [secureDocs, setSecureDocs] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [reviewNotes, setReviewNotes] = useState('');
  const [showDocPreview, setShowDocPreview] = useState(false);

  useEffect(() => {
    if (!user || user.role?.toLowerCase() !== 'lawyer') {
      navigate('/login/staff');
      return;
    }
    fetchCases();
  }, [user, navigate, activeTab]);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/lawyer/cases?filter=${activeTab}`);
      setCases(data.cases || []);
    } catch (err) {
      toast.error('Failed to fetch cases');
    } finally {
      setLoading(false);
    }
  };

  const selectCase = async (caseItem) => {
    setSelectedCase(caseItem);
    setSidebarOpen(false);
    setSecureDocs([]);
    setChatHistory([]);
    try {
      const { data } = await api.get(`/cases/${caseItem.id}/document`);
      setDocumentHtml(data.document?.content);
    } catch (err) {
      setDocumentHtml(null);
    }
    try {
      const { data } = await api.get(`/documents/${caseItem.id}/list`);
      setSecureDocs(data.documents || []);
    } catch (err) {}
    try {
      const { data } = await api.get(`/chat/history/${caseItem.id}`);
      setChatHistory(data.messages || []);
    } catch (err) {}
  };

  const updateCaseStatus = async (status) => {
    if (!selectedCase) return;
    try {
      await api.patch(`/cases/${selectedCase.id}`, { status, review_notes: reviewNotes });
      fetchCases();
      setSelectedCase({ ...selectedCase, status });
      toast.success('Case status updated');
    } catch (err) {
      toast.error('Failed to update case');
    }
  };

  const downloadDocument = () => {
    if (!documentHtml) return;
    const blob = new Blob([documentHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `document_${selectedCase.id?.slice(0, 8)}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredCases = cases.filter(c => {
    if (activeTab === 'assigned') {
      const isAssignedToMe = c.assigned_to === user?.email || c.assigned_to === user?.id;
      return isAssignedToMe && (c.status === 'assigned' || c.status === 'draft');
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0b141a] text-white">
      {/* Mobile Header */}
      <div className="lg:hidden bg-[#111b21] border-b border-[#2a3942] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#00a884] rounded-lg flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="font-bold">Lawyer Portal</span>
            <p className="text-xs text-[#8696a0]">{user?.email}</p>
          </div>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar - Case List */}
        <aside className={`${sidebarOpen ? 'fixed inset-0 z-50 bg-[#111b21]' : 'hidden'} lg:block lg:static lg:w-80 lg:h-screen lg:border-r lg:border-[#2a3942] flex-shrink-0 overflow-y-auto`}>
          <div className="p-4">
            <div className="hidden lg:flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#00a884] rounded-xl flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-bold">Lawyer</h1>
                  <p className="text-xs text-[#8696a0]">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              <button 
                onClick={() => setActiveTab('assigned')}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition ${
                  activeTab === 'assigned' ? 'bg-[#00a884] text-white' : 'bg-[#202c33] text-[#8696a0]'
                }`}
              >
                Assigned
              </button>
              <button 
                onClick={() => setActiveTab('all')}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition ${
                  activeTab === 'all' ? 'bg-[#00a884] text-white' : 'bg-[#202c33] text-[#8696a0]'
                }`}
              >
                All Cases
              </button>
            </div>

            {/* Case List */}
            <div className="space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#00a884]" />
                </div>
              ) : filteredCases.length === 0 ? (
                <div className="text-center py-8 text-[#8696a0] text-sm">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No cases found
                </div>
              ) : (
                filteredCases.map(c => (
                  <CaseCard key={c.id} caseItem={c} onSelect={selectCase} />
                ))
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-[#2a3942]">
              <button 
                onClick={logout}
                className="flex items-center gap-3 px-4 py-3 text-[#aebac1] hover:text-red-400 transition w-full"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content - Case Detail */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {!selectedCase ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Briefcase className="w-16 h-16 text-[#374045] mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Select a Case</h2>
              <p className="text-[#8696a0] text-sm">Choose a case from the sidebar to review and manage</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Case Header */}
              <div className="flex items-start justify-between">
                <div>
                  <button 
                    onClick={() => setSelectedCase(null)}
                    className="lg:hidden flex items-center gap-1 text-[#8696a0] text-sm mb-2"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <h2 className="text-2xl font-bold text-white">{selectedCase.service_type || 'General'} Case</h2>
                  <p className="text-[#8696a0] text-sm mt-1 font-mono">{selectedCase.case_number || selectedCase.id?.slice(0, 8)}</p>
                </div>
                <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  selectedCase.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                  selectedCase.status === 'review' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-purple-500/20 text-purple-400'
                }`}>
                  {selectedCase.status}
                </span>
              </div>

              {/* Client Info */}
              <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#00a884]" /> Client Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-[#8696a0]" />
                    <div>
                      <p className="text-[#8696a0] text-xs">Name</p>
                      <p className="text-white text-sm">{selectedCase.client_name || 'Anonymous'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-[#8696a0]" />
                    <div>
                      <p className="text-[#8696a0] text-xs">Mobile</p>
                      <p className="text-white text-sm">{selectedCase.client_mobile || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-[#8696a0]" />
                    <div>
                      <p className="text-[#8696a0] text-xs">Location</p>
                      <p className="text-white text-sm">{selectedCase.city || 'Unknown'}, {selectedCase.state || 'Unknown'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-[#8696a0]" />
                    <div>
                      <p className="text-[#8696a0] text-xs">Created</p>
                      <p className="text-white text-sm">{new Date(selectedCase.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Secure Uploaded Documents */}
              {secureDocs.length > 0 && (
                <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#00a884]" /> Client Documents ({secureDocs.length})
                  </h3>
                  <div className="space-y-2">
                    {secureDocs.map((d) => (
                      <div key={d.id} className="flex items-center justify-between bg-[#202c33] rounded-lg px-3 py-2">
                        <div>
                          <p className="text-white text-sm">{d.display_name || d.file_name}</p>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-[#8696a0] text-xs font-mono uppercase">{d.category}</span>
                            <span className={`text-xs ${d.encrypted !== false ? 'text-green-400' : 'text-yellow-400'}`}>
                              {d.encrypted !== false ? 'Encrypted' : 'Unencrypted'}
                            </span>
                            {d.retention_until && (
                              <span className="text-[#8696a0] text-xs">
                                Retention: {new Date(d.retention_until).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <a
                          href={`/api/documents/${d.id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:text-blue-300 underline"
                        >
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat History */}
              {chatHistory.length > 0 && (
                <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#00a884]" /> Chat History ({chatHistory.length})
                  </h3>
                  <div className="max-h-64 overflow-y-auto space-y-3">
                    {chatHistory.map((msg, i) => (
                      <div key={i} className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] px-3 py-2 text-sm rounded-xl ${
                          msg.sender_type === 'client'
                            ? 'bg-[#00a884]/20 text-white rounded-tr-sm'
                            : 'bg-[#202c33] text-white rounded-tl-sm'
                        }`}>
                          {msg.content}
                          <div className="text-[10px] text-[#8696a0] mt-1 font-mono">
                            {msg.sender_type} {msg.created_at ? new Date(msg.created_at).toLocaleTimeString() : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Document Preview */}
              {documentHtml && (
                <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#00a884]" /> Generated Document
                    </h3>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setShowDocPreview(true)}
                        className="px-3 py-1.5 bg-[#202c33] text-white text-xs rounded-lg hover:bg-[#2a3942] transition"
                      >
                        Full Preview
                      </button>
                      <button 
                        onClick={downloadDocument}
                        className="px-3 py-1.5 bg-[#00a884] text-white text-xs rounded-lg hover:bg-[#06cf9c] transition flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" /> Download
                      </button>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg overflow-hidden h-64">
                    <iframe srcDoc={documentHtml} className="w-full h-full" title="Document Preview" />
                  </div>
                </div>
              )}

              {/* Review Notes */}
              <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#00a884]" /> Review Notes
                </h3>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="w-full bg-[#202c33] text-white border border-[#374045] rounded-lg px-3 py-2 text-sm min-h-[100px] resize-none focus:outline-none focus:border-[#00a884]"
                  placeholder="Add your review notes, corrections, or feedback..."
                />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                {selectedCase.status === 'assigned' && (
                  <button 
                    onClick={() => updateCaseStatus('review')}
                    className="px-5 py-2.5 bg-[#00a884] text-white rounded-lg text-sm font-medium hover:bg-[#06cf9c] transition flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" /> Mark as Reviewed
                  </button>
                )}
                {selectedCase.status === 'review' && (
                  <button 
                    onClick={() => updateCaseStatus('completed')}
                    className="px-5 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-500 transition flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" /> Complete Case
                  </button>
                )}
                <button 
                  onClick={() => updateCaseStatus('draft')}
                  className="px-5 py-2.5 bg-[#202c33] text-white rounded-lg text-sm font-medium hover:bg-[#2a3942] transition flex items-center gap-2"
                >
                  <AlertCircle className="w-4 h-4" /> Send Back to Draft
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Full Document Preview Modal */}
      {showDocPreview && documentHtml && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
          <div className="bg-[#202c33] px-4 py-3 flex items-center justify-between">
            <span className="text-white font-medium">Document Preview</span>
            <div className="flex gap-2">
              <button onClick={downloadDocument} className="px-3 py-1.5 bg-[#00a884] text-white text-xs rounded-lg flex items-center gap-1">
                <Download className="w-3 h-3" /> Download
              </button>
              <button onClick={() => setShowDocPreview(false)} className="text-[#8696a0] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto bg-white">
            <iframe srcDoc={documentHtml} className="w-full h-full" title="Full Document Preview" />
          </div>
        </div>
      )}
    </div>
  );
}
